import { ArrowBackIosNewRounded, CloseRounded, FullscreenRounded, FullscreenExitRounded } from '@mui/icons-material';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import YouTubeVideoPlayer from '../components/YouTubeVideoPlayer';
import { useVideoNavigation } from '../utils/videoNavigation';
import { landscapeCloseInsets } from '../utils/videoLayout';
import './video.css';

export const Video = ({ userId }) => {
    const { trackId, videoId } = useParams();
    const location = useLocation();
    const { closeVideo } = useVideoNavigation();
    const [track, setTrack] = useState(location.state?.videoTrack);
    const [playback, setPlayback] = useState('loading');
    const [visible, setVisible] = useState(true);
    const [interaction, setInteraction] = useState(0);
    const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [keyboard, setKeyboard] = useState(false);
    const [windowActive, setWindowActive] = useState(true);
    const [screen, setScreen] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const closeRef = useRef(null);
    const openingAccount = useRef(userId);
    const landscape = viewport.width > viewport.height;
    const supportsFullscreen = typeof document.documentElement.requestFullscreen === 'function' && document.fullscreenEnabled !== false;
    const back = () => closeVideo(trackId);
    const reveal = useCallback(() => {
        setVisible(true);
        setInteraction(value => value + 1);
    }, []);

    useEffect(() => {
        if (openingAccount.current !== userId) closeVideo(trackId);
    }, [userId, closeVideo, trackId]);

    useEffect(() => {
        const update = () => setFullscreen(document.fullscreenElement === screen);
        document.addEventListener('fullscreenchange', update);
        return () => document.removeEventListener('fullscreenchange', update);
    }, [screen]);

    const toggleFullscreen = async () => {
        try {
            if (document.fullscreenElement === screen) await document.exitFullscreen();
            else await screen?.requestFullscreen();
        } catch {
            // The player stays usable when the browser denies fullscreen.
        }
    };

    useEffect(() => {
        if (track?.id === trackId) return undefined;
        let active = true;
        axios.get(`/track/${encodeURIComponent(trackId)}`).then(({ data }) => {
            if (active && data?.id === trackId) setTrack(data);
        }).catch(() => {});
        return () => { active = false; };
    }, [trackId, track]);

    useLayoutEffect(() => {
        if (!screen) return undefined;
        const measure = () => {
            const bounds = screen.getBoundingClientRect();
            const style = window.getComputedStyle(screen);
            setViewport({
                width: bounds.width || window.innerWidth,
                height: bounds.height || window.innerHeight,
                safeLeft: parseFloat(style.getPropertyValue('--video-safe-left')) || 0,
                safeTop: parseFloat(style.getPropertyValue('--video-safe-top')) || 0,
            });
            reveal();
        };
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(screen);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [screen, reveal]);

    // Cross-origin iframe taps do not bubble to this document. Keep a reachable
    // exit on touch devices; desktop movement outside the iframe restores it.
    const canHide = landscape && playback === 'playing' && !keyboard && windowActive &&
        window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
    useEffect(() => {
        setVisible(true);
        if (!canHide || document.hidden || document.activeElement === closeRef.current) return undefined;
        const timer = window.setTimeout(() => setVisible(false), 3000);
        return () => window.clearTimeout(timer);
    }, [canHide, interaction]);

    useEffect(() => {
        let focusCheck;
        const blur = () => {
            // Focusing the embedded player can blur the parent window while
            // the document still has focus. That is not an inactive app window.
            reveal();
            window.clearTimeout(focusCheck);
            focusCheck = window.setTimeout(() => setWindowActive(document.hasFocus()), 0);
        };
        const focus = () => { setWindowActive(true); reveal(); };
        window.addEventListener('blur', blur);
        window.addEventListener('focus', focus);
        document.addEventListener('visibilitychange', reveal);
        return () => {
            window.clearTimeout(focusCheck);
            window.removeEventListener('blur', blur);
            window.removeEventListener('focus', focus);
            document.removeEventListener('visibilitychange', reveal);
        };
    }, [reveal]);

    const closeStyle = landscape ? landscapeCloseInsets(viewport.width, viewport.height, viewport.safeLeft, viewport.safeTop) : undefined;
    return <Modal open className='appVideoOverlay' onClose={(_, reason) => { if (reason === 'escapeKeyDown') back(); }}>
        <section ref={setScreen} className={`episodeVideoScreen${landscape ? ' episodeVideoLandscape' : ''}`}
            role='dialog' aria-modal='true' aria-label='Episode video' tabIndex={-1}
            onPointerMove={reveal} onPointerDown={reveal} onKeyDown={() => { setKeyboard(true); reveal(); }}>
            <button ref={closeRef} className={`episodeVideoBack${canHide && !visible ? ' episodeVideoBackHidden' : ''}`}
                style={closeStyle} type='button' aria-label='Back to episode' onFocus={reveal} onClick={back}>
                {landscape ? <CloseRounded aria-hidden='true' /> : <ArrowBackIosNewRounded aria-hidden='true' />}
            </button>
            {supportsFullscreen && <button className={`episodeVideoFullscreen${canHide && !visible ? ' episodeVideoBackHidden' : ''}`}
                type='button' aria-label={fullscreen ? 'Exit full screen' : 'Full screen'} onFocus={reveal} onClick={toggleFullscreen}>
                {fullscreen ? <FullscreenExitRounded aria-hidden='true' /> : <FullscreenRounded aria-hidden='true' />}
            </button>}
            {/^[A-Za-z0-9_-]{11}$/.test(videoId) ? <YouTubeVideoPlayer videoId={videoId} userId={userId} track={track}
                initialSeconds={location.state?.videoStartSeconds} autoPlay={location.state?.videoAutoPlay === true}
                audioContinuationSeconds={location.state?.videoContinuationUserId === (userId || null) ? location.state?.videoAudioContinuationSeconds : null}
                nativeFullscreen={!supportsFullscreen} onPlaybackState={setPlayback} />
                : <p className='episodeVideoInvalid' role='status'>Video unavailable</p>}
        </section>
    </Modal>;
};
export default connect(state => ({ userId: state.user.authenticated ? state.user.uid : null }))(Video);
